import { makeQueryOptions } from '@mint-lib/api-zod-support/react-query';

import {
  defaultApi,
  defaultClient,
  type baseResponseSchema,
} from '@mint-sdk/api-core';
import { z } from 'zod';

import type { flightVersionSchema } from '../shared/schemas/flights.js';
import { GetMediaDataResponseSchema } from '../shared/schemas/mediaData.js';
import {
  CreatePlanRequestSchema,
  CreatePlanResponseSchema,
  GetActivitiesPreviewQuerySchema,
  GetActivitiesPreviewResponseSchema,
  GetMediaPlansPreviewQuerySchema,
  GetMediaPlansPreviewResponseSchema,
  GetPlanResponseSchema,
  PlanNameValidationQuerySchema,
  PlanNameValidationResponseSchema,
  RemoveMediaPlansRequestSchema,
  UpdatePlanRequestSchema,
  UpdatePlanResponseSchema,
} from '../shared/schemas/mediaPlan.js';

export * from '../activity/index.js';
export * from '../budgetingStructures/index.js';
export * from '../columns/index.js';
export * from '../file/index.js';
export * from '../flights/index.js';
export * from '../mediaData/index.js';
export * from '../mediaItem/index.js';
export * from '../metadata/index.js';
export * from '../myPlans/index.js';
export * from '../restrictions/index.js';
export * from '../rows/index.js';
export * from '../sidebarTree/index.js';
export * from '../summary/index.js';

export type CreatePlanRequest = z.infer<typeof CreatePlanRequestSchema>;
export type UpdatePlanRequest = z.infer<typeof UpdatePlanRequestSchema>;
export type UpdatePlanResponse = z.infer<typeof UpdatePlanResponseSchema>;
export type GetPlanResponse = z.infer<typeof GetPlanResponseSchema>;
export type BaseResponseSchema = z.infer<typeof baseResponseSchema>;
export type GetMediaPlansPreviewResponse = z.infer<
  typeof GetMediaPlansPreviewResponseSchema
>;
export type GetActivitiesPreviewResponse = z.infer<
  typeof GetActivitiesPreviewResponseSchema
>;
export type FlightVersionItem = z.infer<typeof flightVersionSchema>;

export const create = defaultApi.declareEndpoint({
  url: 'planning-service/media-plans/',
  method: 'POST',
  requestSchema: CreatePlanRequestSchema,
  responseSchema: CreatePlanResponseSchema,
});

export const validateName = defaultApi.declareEndpoint({
  url: 'planning-service/media-plans/validate-name/',
  method: 'GET',
  querySchema: PlanNameValidationQuerySchema,
  responseSchema: PlanNameValidationResponseSchema,
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const validateNameQueryOptions = makeQueryOptions(validateName, {
  processResponse(data) {
    return data;
  },
});

export const get = defaultApi.declareEndpoint({
  url: 'planning-service/media-plans/$planId/' as const,
  method: 'GET',
  responseSchema: GetPlanResponseSchema,
});

export const getPlanQueryOptions = makeQueryOptions(get, {
  processResponse(data) {
    return data;
  },
});

export const bulkDelete = defaultApi.declareEndpoint({
  url: 'planning-service/media-plans/delete_batch/' as const,
  method: 'POST',
  requestSchema: RemoveMediaPlansRequestSchema,
  responseSchema: z.string(),
});

export const update = defaultApi.declareEndpoint({
  url: 'planning-service/media-plans/$planId/' as const,
  method: 'PATCH',
  requestSchema: UpdatePlanRequestSchema,
  responseSchema: UpdatePlanResponseSchema,
});

export const getTotalsRowQueryOptions = defaultClient.query({
  url: 'planning-service/media-plans/$planId/totals/' as const,
  method: 'GET',
  responseSchema: GetMediaDataResponseSchema,
  processResponse(data) {
    return data.data?.items[0];
  },
});

export const getMediaPlansPreviewQueryOptions = defaultClient.query({
  url: 'planning-service/media-plans/preview/' as const,
  method: 'GET',
  querySchema: GetMediaPlansPreviewQuerySchema,
  responseSchema: GetMediaPlansPreviewResponseSchema,
  processResponse(data) {
    return data.data.items;
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const getActivitiesPreviewQueryOptions = defaultClient.query({
  url: 'planning-service/activities/preview/' as const,
  method: 'GET',
  querySchema: GetActivitiesPreviewQuerySchema,
  responseSchema: GetActivitiesPreviewResponseSchema,
  processResponse(data) {
    return data.data.items;
  },
  clientOptions: {
    request: {
      snakeCaseQueryParams: true,
    },
  },
});

export const exportMediaPlan = defaultApi.declareStream({
  url: 'planning-service/media-plans/$mediaPlanId/export/' as const,
  method: 'POST',
});
